import {
  format,
  isBefore,
  isSameDay,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { type Ref, useId, useMemo, useState } from 'react';
import { type DateRange, type Matcher } from 'react-day-picker';
import { useTranslate } from 'ra-core';
import { Calendar } from '../ui/calendar';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';

export interface DateRangeFieldProps {
  startDate: string | undefined;
  endDate: string | undefined;
  onStartDateChange: (date: string | undefined) => void;
  onEndDateChange: (date: string | undefined) => void;
  label?: string;
  minDate?: string;
  maxDate?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  required?: boolean;
  inputId?: string;
  labelId?: string;
  ariaDescribedBy?: string;
  inputRef?: Ref<HTMLButtonElement>;
}

export function DateRangeField({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  label,
  minDate,
  maxDate,
  disabled,
  readOnly,
  error,
  required,
  inputId,
  labelId,
  ariaDescribedBy,
  inputRef,
}: DateRangeFieldProps) {
  const translate = useTranslate();
  const generatedId = useId();
  const [open, setOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>();
  const [previewDate, setPreviewDate] = useState<Date | undefined>();
  const [month, setMonth] = useState<Date>();

  const parsedStartDate = useMemo(
    () => parseDateString(startDate),
    [startDate]
  );
  const parsedEndDate = useMemo(() => parseDateString(endDate), [endDate]);
  const parsedMinDate = useMemo(() => parseDateString(minDate), [minDate]);
  const parsedMaxDate = useMemo(() => parseDateString(maxDate), [maxDate]);

  const displayValue = useMemo(() => {
    if (!parsedStartDate || !parsedEndDate) {
      return undefined;
    }

    if (isSameDay(parsedStartDate, parsedEndDate)) {
      return format(parsedStartDate, 'MMM d, yyyy');
    }

    return `${format(parsedStartDate, 'MMM d, yyyy')} - ${format(
      parsedEndDate,
      'MMM d, yyyy'
    )}`;
  }, [parsedEndDate, parsedStartDate]);

  const draftStart = draftRange?.from;
  const draftEnd = draftRange?.to;
  const previewRange =
    draftStart && !draftEnd && previewDate
      ? {
          from: isBefore(previewDate, draftStart) ? previewDate : draftStart,
          to: isBefore(previewDate, draftStart) ? draftStart : previewDate,
        }
      : undefined;

  const disabledDays = useMemo<Matcher[] | undefined>(() => {
    const matchers: Matcher[] = [];

    if (parsedMinDate) {
      matchers.push({ before: parsedMinDate });
    }
    if (parsedMaxDate) {
      matchers.push({ after: parsedMaxDate });
    }

    return matchers.length > 0 ? matchers : undefined;
  }, [parsedMaxDate, parsedMinDate]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled || readOnly) {
      return;
    }

    if (nextOpen) {
      setDraftRange(
        parsedStartDate
          ? { from: parsedStartDate, to: parsedEndDate ?? undefined }
          : undefined
      );
      setPreviewDate(undefined);
      setMonth(parsedEndDate ?? parsedStartDate ?? parsedMaxDate ?? new Date());
    } else {
      setDraftRange(undefined);
      setPreviewDate(undefined);
    }

    setOpen(nextOpen);
  };

  const handleDateChange = (date: Date) => {
    const nextDate = startOfDay(date);

    if (!draftStart || draftEnd) {
      setDraftRange({ from: nextDate });
      setPreviewDate(undefined);
      return;
    }

    const start = isBefore(nextDate, draftStart) ? nextDate : draftStart;
    const end = isBefore(nextDate, draftStart) ? draftStart : nextDate;

    setDraftRange({ from: start, to: end });
    onStartDateChange(toDateInputValue(start));
    onEndDateChange(toDateInputValue(end));
    handleOpenChange(false);
  };

  const handleClear = () => {
    onStartDateChange(undefined);
    onEndDateChange(undefined);
    handleOpenChange(false);
  };

  const id = inputId ?? generatedId;
  const triggerLabel = labelId
    ? undefined
    : (label ?? translate('moonstar.dateRange.label', { _: 'Date range' }));

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          <Button
            ref={inputRef}
            id={id}
            type="button"
            variant="outline"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-labelledby={labelId}
            aria-label={triggerLabel}
            aria-describedby={ariaDescribedBy}
            aria-invalid={error || undefined}
            disabled={disabled || readOnly}
            className={cn(
              'w-full justify-between bg-background text-left font-normal',
              !displayValue && 'text-muted-foreground',
              error && 'border-destructive ring-destructive/20'
            )}
          />
        }
      >
        <span className="truncate">{displayValue ?? ''}</span>
        <CalendarDays
          className={cn('ml-2 size-4 shrink-0', open && 'text-primary')}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="range"
          selected={draftRange}
          onSelect={(_, triggerDate) => handleDateChange(triggerDate)}
          onMonthChange={setMonth}
          month={month}
          disabled={disabledDays}
          fixedWeeks
          showOutsideDays
          modifiers={previewRange ? { preview: previewRange } : undefined}
          modifiersClassNames={{
            preview: 'bg-primary/20 text-foreground',
          }}
          onDayMouseEnter={(date) => {
            if (draftStart && !draftEnd) {
              setPreviewDate(startOfDay(date));
            }
          }}
          onDayMouseLeave={() => setPreviewDate(undefined)}
        />
        {!required && (
          <div className="border-t px-3 py-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
            >
              {translate('moonstar.dateRange.clear', { _: 'Clear' })}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function parseDateString(value?: string): Date | null {
  if (!value) {
    return null;
  }

  const date = startOfDay(parseISO(value));
  return isValid(date) ? date : null;
}

function toDateInputValue(value: Date): string {
  return format(value, 'yyyy-MM-dd');
}
